/**
 * Create featured (custom) segment modal.
 *
 * Collects name / description / segment+region+RFM+min-spent criteria and
 * calls useCreateSegment. On success the parent page refetches its segments
 * list (handled via the mutation's query invalidation).
 *
 * Extracted from Segments.tsx during the Maintainability Sprint 2026-04-19
 * (Task 1.1 step 1.1.8). Behavior, French copy, and the inline region /
 * segment / RFM option lists are preserved byte-for-byte. The inline option
 * lists intentionally do NOT reuse REGIONS from ./constants.ts — resolving
 * that duplication is out of scope for this refactor (YAGNI).
 */

import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@cieda/djayma-ui";
import { useCreateSegment } from "@/hooks/admin/useCRM";

interface CreateSegmentModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateSegmentModal({
  open,
  onClose,
}: CreateSegmentModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [criteriaSegment, setCriteriaSegment] = useState("");
  const [criteriaRegion, setCriteriaRegion] = useState("");
  const [criteriaRfm, setCriteriaRfm] = useState("");
  const [minSpent, setMinSpent] = useState("");
  const createMutation = useCreateSegment();

  const handleSubmit = () => {
    const criteria: Record<string, unknown> = {};
    if (criteriaSegment) criteria.segment = criteriaSegment;
    if (criteriaRegion) criteria.region = criteriaRegion;
    if (criteriaRfm) criteria.rfm_segment = criteriaRfm;
    if (minSpent) criteria.min_total_spent_fcfa = Number(minSpent);

    createMutation.mutate(
      { name: name.trim(), description: description.trim() || undefined, criteria },
      {
        onSuccess: () => {
          toast.success("Segment créé avec succès");
          onClose();
          setName("");
          setDescription("");
          setCriteriaSegment("");
          setCriteriaRegion("");
          setCriteriaRfm("");
          setMinSpent("");
        },
        onError: () => {
          toast.error("Erreur lors de la création du segment");
        },
      },
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-stone-900">
            Créer un segment personnalisé
          </h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Nom *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: VIP Dakar"
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description optionnelle"
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">
                Segment
              </label>
              <select
                value={criteriaSegment}
                onChange={(e) => setCriteriaSegment(e.target.value)}
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
              >
                <option value="">Tous</option>
                <option value="vip">VIP</option>
                <option value="active">Actifs</option>
                <option value="new">Nouveaux</option>
                <option value="wholesale">Grossistes</option>
                <option value="at_risk">À risque</option>
                <option value="churned">Perdus</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">
                Région
              </label>
              <select
                value={criteriaRegion}
                onChange={(e) => setCriteriaRegion(e.target.value)}
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
              >
                <option value="">Toutes</option>
                <option value="Dakar">Dakar</option>
                <option value="Kaolack">Kaolack</option>
                <option value="Saint-Louis">Saint-Louis</option>
                <option value="Fatick">Fatick</option>
                <option value="Thies-Mbour">Thiès-Mbour</option>
                <option value="Diourbel-Touba">Diourbel-Touba</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">
                RFM
              </label>
              <select
                value={criteriaRfm}
                onChange={(e) => setCriteriaRfm(e.target.value)}
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
              >
                <option value="">Tous</option>
                <option value="champions">Champions</option>
                <option value="fideles">Fidèles</option>
                <option value="a_risque">À risque</option>
                <option value="perdus">Perdus</option>
                <option value="inactifs">Inactifs</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">
                Dépenses min (FCFA)
              </label>
              <input
                type="number"
                value={minSpent}
                onChange={(e) => setMinSpent(e.target.value)}
                placeholder="0"
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
                min={0}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? "Création..." : "Créer le segment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
